/**
 * CycleSelector - Dropdown to assign record to a specific cycle
 */

import { cn } from '@/lib/cn';
import { ChevronDown, Fish, Calendar } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

interface CycleOption {
  id: string;
  pond_id: string;
  pond_name: string;
  stocking_date: string;
}

interface CycleSelectorProps {
  cycles: CycleOption[];
  value?: string;
  onChange: (cycleId: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function CycleSelector({
  cycles,
  value,
  onChange,
  placeholder = 'Select cycle...',
  className,
  disabled = false,
}: CycleSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedCycle = cycles.find(c => c.id === value);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Group cycles by pond
  const cyclesByPond = cycles.reduce((acc, cycle) => {
    if (!acc[cycle.pond_name]) {
      acc[cycle.pond_name] = [];
    }
    acc[cycle.pond_name].push(cycle);
    return acc;
  }, {} as Record<string, CycleOption[]>);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between gap-2',
          'px-3 py-2 rounded-lg',
          'bg-white border border-gray-200',
          'text-sm text-left',
          'transition-all duration-150',
          'hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
          disabled && 'opacity-50 cursor-not-allowed bg-gray-50',
          isOpen && 'border-primary-500 ring-2 ring-primary-500/20'
        )}
      >
        <span className="flex items-center gap-2 truncate">
          {selectedCycle ? (
            <>
              <Fish className="w-4 h-4 text-primary-500 shrink-0" />
              <span className="font-medium">{selectedCycle.pond_name}</span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-500 text-xs">
                Started {new Date(selectedCycle.stocking_date).toLocaleDateString()}
              </span>
            </>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-gray-400 shrink-0 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={cn(
            'absolute z-50 w-full mt-1',
            'bg-white rounded-lg border border-gray-200 shadow-lg',
            'max-h-64 overflow-y-auto',
            'animate-in fade-in slide-in-from-top-1 duration-150'
          )}
        >
          {cycles.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-500 text-center">
              No active cycles found
            </div>
          ) : (
            Object.entries(cyclesByPond).map(([pondName, pondCycles]) => (
              <div key={pondName}>
                {/* Pond header */}
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                  {pondName}
                </div>
                {/* Cycles for this pond */}
                {pondCycles.map(cycle => (
                  <button
                    key={cycle.id}
                    type="button"
                    onClick={() => {
                      onChange(cycle.id);
                      setIsOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2',
                      'text-sm text-left',
                      'hover:bg-primary-50 transition-colors',
                      value === cycle.id && 'bg-primary-50 text-primary-700'
                    )}
                  >
                    <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="flex-1">
                      <span className="text-gray-500">Stocked </span>
                      <span className="font-medium">
                        {new Date(cycle.stocking_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </span>
                    {value === cycle.id && (
                      <span className="text-xs font-medium text-primary-600 bg-primary-100 px-1.5 py-0.5 rounded">
                        Selected
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
